<template>
    <div>
        <mc-configform
            :label-width="140"
            :fields="[{
                prop: 'productContentProducer',
                label: '商品内容制作方式',
                slot: 'productContentProducer',
                labelPosition: 'required',
            }]"
        >
            <template slot="productContentProducer">
                <mc-choice
                    v-model="modelInstance.productContentProducer"
                    type="radio"
                    :showCheckAll="false"
                    :source="makingWayList"
                    :disabled="MixinJudgeRejectedAttrsDisabled('productContentProducer')"
                    style="height: 32px;"
                />
            </template>
        </mc-configform>
        <operate-content
            ref="operateContent"
            refer="createNewPage"
            mode="edit"
            :is-content-section-required="isContentSectionRequired"
            :outer-rules="rules"
            :sku-id="skuId"
            :flow-id="applyId"
            @resubmission="resubmission"
        />
    </div>
</template>

<script>
import emitter from 'utils/emitter';
import Rules from '@lsfe/rules';
import OperateContent from '../../OperateContent';
import { shared, operateInfoToken, createNewToken } from '../shareState';
import { getBaseModel } from '../baseModel';
import MixinJudgeRejectedAttrsDisabled from '../mixins/rejectedDisabled';

const baseModel = getBaseModel();

const CONTENT_MAKING_WAY_LIST = [
    {
        value: 1,
        label: '供应商自行制作',
        key: 'supplier',
    },
    {
        value: 2,
        label: '申请小象代为制作',
        key: 'xiaoxiang',
    },
];

const FORM_KEYS = ['subTitle', 'skuSellPointContentList', 'lbPicture', 'dlbPicture', 'video', 'mediumDescription', 'skuPictureList'];

const rules = {
    subTitle: [
        Rules.isRequired('副标题必填'),
        Rules.lenRange({ max: 44 }, '最多44个字'),
    ],
    skuSellPointContentList: [
        Rules.isRequired('商品卖点必填'),
        Rules.lenRange({ min: 2, max: 6 }, '大于2个小于6个'),
    ],
    lbPicture: [
        Rules.isRequired('列表用图必填'),
    ],
    skuPictureList: [
        Rules.isRequired('商品主图必填'),
        Rules.lenRange({ min: 1 }, '商品主图必填'),
    ],
};

export default {
    name: 'OperateContentManage',
    components: {
        OperateContent,
    },
    mixins: [emitter, MixinJudgeRejectedAttrsDisabled],
    data() {
        return {
            createInfoModel: {},
            isContentSectionRequired: false,
            rules: {},

            makingWayList: CONTENT_MAKING_WAY_LIST,
        };
    },
    computed: {
        modelInstance() {
            return baseModel.models;
        },
        categoryId() {
            return this.createInfoModel.categoryId;
        },
        skuId() {
            return this.createInfoModel?.skuBaseId || baseModel.models?.presetSkuId;
        },
        applyId() {
            return baseModel.models.applyId;
        },
    },
    async activated() {
        if (shared.stepChangeType === 'next') {
            try {
                await this.initState();
            } catch (error) {
                console.warn(error);
            } finally {
                this.dispatch(createNewToken, 'page-loaded', true);
            }
        }
    },
    created() {
        baseModel.registerVM(this, operateInfoToken);
    },
    methods: {
        async initState() {
            const draft = await this.fetchNewCreateData(baseModel.models.applyId);
            baseModel.setData(draft);

            const operateInfo = baseModel.initOperateInfo;
            this.createInfoModel = baseModel.models.applyInfoSection;
            if (this.$refs?.operateContent) {
                this.$refs.operateContent._initForCreateNew({
                    categoryId: this.createInfoModel.categoryId,
                    bgCategoryId: this.createInfoModel.bgCategoryId,
                    form: operateInfo,
                    skuId: this.createInfoModel.skuBaseId || baseModel.models.presetSkuId || '',
                    createNewReferData: {
                        ...this.createInfoModel,
                        skuExtAttrList: baseModel.models.skuBaseSection.skuBaseAttributes,
                    },
                });
                this.$refs.operateContent.queryNeedNutritions(this.createInfoModel.bgCategoryId);
                this.$refs.operateContent.queryShowSelling(this.createInfoModel.bgCategoryId);
                this.$refs.operateContent.searchAllContentRule(this.createInfoModel.bgCategoryId);
                this.$refs.operateContent.queryNeedTagNutritions(this.createInfoModel.bgCategoryId);
            }
        },
        async fetchNewCreateData(applyId) {
            const draftData = await this.$service.fetchNewSellDetail({ applyId });
            return draftData;
        },
        getVMPostData() {
            return this.$refs.operateContent._getData();
        },
        async validate(type) {
            if (!this.modelInstance.productContentProducer) {
                this.$message.error('商品内容制作方式为必填字段');
                return false;
            }
            const XIAOXIANG_MAKING_WAY = CONTENT_MAKING_WAY_LIST.find((item) => item.key === 'xiaoxiang');
            if (+this.modelInstance.productContentProducer === XIAOXIANG_MAKING_WAY.value) {
                const confirm = await this.$confirm(
                    '系统将通知小象内容运营团队代为制作，若您已填写内容信息系统将自动清空，请确认',
                    '提示',
                ).catch(() => false);
                if (!confirm) return false;
            }
            if (+this.modelInstance.productContentProducer === XIAOXIANG_MAKING_WAY.value) {
                // 小象制作时，内容页无需校验
                return true;
            }

            // 0不展示 1选填 2必填
            // const requiredKeys = Object.keys(REQUIRED_KEYS_MAP);
            const isContentSectionRequired = shared.categoryConfig.flowSpec && shared.categoryConfig.flowSpec.contentSectionRequired === 2;
            this.isContentSectionRequired = isContentSectionRequired;
            const formData = this.getVMPostData();
            if (isContentSectionRequired) {
                // 如果不是确认提交 和 需要识别不需要校验
                if (type !== 'submit' && this.$refs.operateContent?.isNeedNutritions) {
                    if (!await this.$refs.operateContent?.handleValidate()) return false;
                }
                return this.$refs.operateContent.validate();
            } else {
                this.rules = { };
                this.$refs.operateContent.clearValidate();
                // 全部字段不填
                const keysEmpty = FORM_KEYS.every((key) => window._.isEmpty(formData[key]));
                if (keysEmpty) {
                    return true;
                } else {
                    this.rules = { ...rules };
                    // 如果不是确认提交 和 需要识别不需要校验
                    if (type !== 'submit' && this.$refs.operateContent?.isNeedNutritions) {
                        if (!await this.$refs.operateContent?.handleValidate()) return false;
                    }
                    return this.$nextTick().then(() => this.$refs.operateContent.validate());
                }
            }
        },
        // 临时处理，2次计算图片尺寸，保证width/height参数和实际图片尺寸一致
        picSizeRewrite(formData) {
            return this.$refs.operateContent.picSizeRewrite(formData);
        },
        resubmission() {
            this.$emit('resubmission');
        },
    },
};
</script>
